import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  parseJobChatConversationList,
  parseJobChatPagedMessages,
  parseJobChatPostResult,
} from './job-chat-api-parse';

export interface JobChatMessageActionDto {
  id: string;
  label: string;
  type: string;
  jobKey?: string | null;
  estimateKey?: string | null;
}

export interface JobChatMessageDto {
  jobChatMessageKey: string;
  jobKey: string;
  senderType: number;
  senderKey: string;
  vendorKey: string | null;
  senderName: string;
  body: string;
  createdUtc: string;
  seenByRecipient?: boolean;
  seenByRecipientUtc?: string | null;
  actions?: JobChatMessageActionDto[];
  actionsConsumed?: boolean;
}

export interface JobChatPagedResponse {
  items: JobChatMessageDto[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

export interface JobChatThreadReadEventDto {
  jobKey: string;
  threadVendorKey: string | null;
  readerParticipantType: number;
  lastReadUtc: string;
}

export interface JobChatConversationSummaryDto {
  jobKey: string;
  jobLabel: string;
  /** Vendor for this inbox row; null = general job thread (messages with no vendor). */
  vendorKey: string | null;
  vendorLabel: string;
  lastMessagePreview: string;
  lastMessageUtc: string;
  lastSenderType?: number;
  isUnread?: boolean;
  unreadMessageCount?: number;
  messageCount: number;
  /** Latest on-site approval estimate for this job + vendor (null when none). */
  onsiteEstimateKey?: string | null;
  /** On-site state for the inbox filters: approved, declined or in_progress. */
  onsiteState?: 'approved' | 'declined' | 'in_progress' | null;
  /** The job's account manager (personnel key). */
  accountManagerKey?: string | null;
  /** Submitted from the vendor web portal, not the app: no automatic negotiation (Nahid, 7 Oct). */
  submittedFromWeb?: boolean;
}

export interface JobChatConversationListResponse {
  items: JobChatConversationSummaryDto[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalUnreadMessageCount?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LiveChatService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = `${environment.apiBaseUrl}/api/v2/job-chat`;

  /** @param onsiteOnly true = only threads with an on-site approval (the CAS pop-up). */
  getConversations(pageNumber = 1, pageSize = 30, onsiteOnly = false): Observable<JobChatConversationListResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));
    if (onsiteOnly) params = params.set('onsiteOnly', 'true');
    return this.http
      .get<unknown>(`${this.apiBase}/conversations`, { params })
      .pipe(
        map((res) => parseJobChatConversationList(res)),
        catchError(() => of(null)),
      );
  }

  /**
   * @param thread `general` = only lines with no vendor; vendor GUID = that vendor’s thread; omit/`all` = entire job (legacy).
   */
  getMessages(
    jobKey: string,
    pageNumber = 1,
    pageSize = 100,
    thread?: string | null,
  ): Observable<JobChatPagedResponse | null> {
    let params = new HttpParams().set('pageNumber', String(pageNumber)).set('pageSize', String(pageSize));
    if (thread != null && thread !== '') {
      params = params.set('thread', thread);
    }
    return this.http
      .get<unknown>(`${this.apiBase}/jobs/${jobKey}/messages`, { params })
      .pipe(
        map((res) => parseJobChatPagedMessages(res)),
        catchError(() => of(null)),
      );
  }

  postMessage(
    jobKey: string,
    body: string,
    vendorKey?: string | null,
  ): Observable<{ ok: boolean; jobChatMessageKey?: string; senderName?: string }> {
    const payload: { body: string; vendorKey?: string } = { body };
    if (vendorKey) {
      payload.vendorKey = vendorKey;
    }
    return this.http
      .post<unknown>(`${this.apiBase}/jobs/${jobKey}/messages`, payload)
      .pipe(
        map((res) => {
          const data = parseJobChatPostResult(res);
          return data
            ? { ok: true, jobChatMessageKey: data.jobChatMessageKey, senderName: data.senderName }
            : { ok: false };
        }),
        catchError(() => of({ ok: false })),
      );
  }

  /** Mark staff read cursor for a thread (matches v1 mark-read on thread open). */
  /**
   * Tells the vendor RFI is reviewing their estimate — the Estimates tab calls this when an admin opens a
   * negotiation. Job Ops reads the estimate to pick the copy and to decide whether the vendor has already been
   * told (once per estimate, shared with the chat pop-up), so calling it again is harmless and `posted` comes
   * back false. Replaces the legacy `JobOpsChat/GetThreadEstimate?notifyReviewing=1` hop, which needed an Admin
   * Portal V1 session.
   */
  postReviewingNotice(
    jobKey: string,
    vendorKey: string,
    estimateKey: string,
  ): Observable<{ ok: boolean; posted: boolean }> {
    return this.http
      .post<unknown>(`${this.apiBase}/jobs/${jobKey}/reviewing`, { vendorKey, estimateKey })
      .pipe(
        // Job Ops wraps every response in ApiResponse<T>, so the flag is under `data`; the bare shape is
        // accepted too rather than depending on which layer answered.
        map((res) => {
          const body = res as { posted?: boolean; data?: { posted?: boolean } } | null;
          return { ok: true, posted: (body?.data?.posted ?? body?.posted) === true };
        }),
        catchError(() => of({ ok: false, posted: false })),
      );
  }

  markThreadRead(jobKey: string, thread?: string | null): Observable<boolean> {
    let params = new HttpParams();
    if (thread != null && thread !== '') {
      params = params.set('thread', thread);
    }
    return this.http.post<unknown>(`${this.apiBase}/jobs/${jobKey}/read`, {}, { params }).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }
}
